import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type AccessPolicyUpdate,
  clearTrainingModuleAccessPolicy,
  clearTrainingSectionAccessPolicy,
  getTrainingBaseAccessPolicy,
  getTrainingCatalog,
  getTrainingGatingConfig,
  getTrainingGatingRaw,
  getTrainingModuleAccessPolicy,
  getTrainingSectionAccessPolicy,
  saveTrainingGatingRaw,
  type TrainingGatingConfig,
  updateTrainingBaseAccessPolicy,
  updateTrainingGatingConfig,
  updateTrainingModuleAccessPolicy,
  updateTrainingModuleStatus,
  updateTrainingSectionAccessPolicy,
  updateTrainingSectionStatus,
} from "@/api/admin-api"

import { adminQueryKeys } from "../admin-query-keys"
import { FAST_STALE_TIME, GC_TIME } from "./_shared"

// Training catalog (static + Cosmos overrides)
export function useAdminTrainingCatalogQuery(enabled = true) {
  return useQuery({
    queryKey: adminQueryKeys.trainingCatalog(),
    queryFn: () => getTrainingCatalog(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateTrainingModuleStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ moduleId, enabled }: { moduleId: string; enabled: boolean }) => {
      return await updateTrainingModuleStatus(moduleId, enabled)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingCatalog() })
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingStatus() })
    },
  })
}

export function useUpdateTrainingSectionStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      moduleId,
      sectionId,
      enabled,
    }: {
      moduleId: string
      sectionId: string
      enabled: boolean
    }) => {
      return await updateTrainingSectionStatus(moduleId, sectionId, enabled)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingCatalog() })
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingStatus() })
    },
  })
}

export function useAdminTrainingBaseAccessPolicyQuery(enabled = true) {
  return useQuery({
    queryKey: adminQueryKeys.trainingBaseAccess(),
    queryFn: () => getTrainingBaseAccessPolicy(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateTrainingBaseAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ policy }: { policy: AccessPolicyUpdate }) => {
      return await updateTrainingBaseAccessPolicy(policy)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingBaseAccess() })
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingCatalog() })
    },
  })
}

export function useAdminTrainingModuleAccessPolicyQuery(
  moduleId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: adminQueryKeys.trainingModuleAccess(moduleId ?? ""),
    queryFn: () => getTrainingModuleAccessPolicy(moduleId ?? ""),
    enabled: enabled && !!moduleId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateTrainingModuleAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ moduleId, policy }: { moduleId: string; policy: AccessPolicyUpdate }) => {
      return await updateTrainingModuleAccessPolicy(moduleId, policy)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingCatalog() })
      void queryClient.invalidateQueries({
        queryKey: adminQueryKeys.trainingModuleAccess(variables.moduleId),
      })
    },
  })
}

export function useClearTrainingModuleAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ moduleId }: { moduleId: string }) => {
      return await clearTrainingModuleAccessPolicy(moduleId)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingCatalog() })
      void queryClient.invalidateQueries({
        queryKey: adminQueryKeys.trainingModuleAccess(variables.moduleId),
      })
    },
  })
}

export function useAdminTrainingSectionAccessPolicyQuery(
  moduleId: string | undefined,
  sectionId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: adminQueryKeys.trainingSectionAccess(moduleId ?? "", sectionId ?? ""),
    queryFn: () => getTrainingSectionAccessPolicy(moduleId ?? "", sectionId ?? ""),
    enabled: enabled && !!moduleId && !!sectionId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateTrainingSectionAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      moduleId,
      sectionId,
      policy,
    }: {
      moduleId: string
      sectionId: string
      policy: AccessPolicyUpdate
    }) => {
      return await updateTrainingSectionAccessPolicy(moduleId, sectionId, policy)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingCatalog() })
      void queryClient.invalidateQueries({
        queryKey: adminQueryKeys.trainingSectionAccess(variables.moduleId, variables.sectionId),
      })
    },
  })
}

export function useClearTrainingSectionAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ moduleId, sectionId }: { moduleId: string; sectionId: string }) => {
      return await clearTrainingSectionAccessPolicy(moduleId, sectionId)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingCatalog() })
      void queryClient.invalidateQueries({
        queryKey: adminQueryKeys.trainingSectionAccess(variables.moduleId, variables.sectionId),
      })
    },
  })
}

export function useAdminTrainingGatingQuery(enabled = true) {
  return useQuery({
    queryKey: adminQueryKeys.trainingGating(),
    queryFn: getTrainingGatingConfig,
    enabled,
  })
}

export function useUpdateTrainingGatingMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (config: TrainingGatingConfig) => updateTrainingGatingConfig(config),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingGating() })
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingCatalog() })
    },
  })
}

export function useTrainingGatingRawQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "training", "gating", "raw"],
    queryFn: getTrainingGatingRaw,
    enabled,
    staleTime: 0,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateTrainingGatingRawMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      entity,
      expectedHash,
      reason,
    }: {
      entity: { required_module_ids: string[] }
      expectedHash: string
      reason: string
    }) => saveTrainingGatingRaw({ entity, expectedHash, reason }),
    onSuccess: () => {
      // adminQueryKeys.trainingGating() resolves to ["admin","training","gating"]
      // — the gating list view's key. The raw query uses a sibling
      // ["admin","training","gating","raw"] key.
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingGating() })
      void queryClient.invalidateQueries({ queryKey: ["admin", "training", "gating", "raw"] })
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.trainingCatalog() })
    },
  })
}
